import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const DEFAULT_START_DATE = '2025-06-01';

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.detail || `Request failed (${response.status})`);
  return payload;
}

function money(value, { signed = false } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;
  const formatted = Math.abs(amount).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
  if (!signed || amount === 0) return formatted;
  return `${amount > 0 ? '+' : '−'}${formatted}`;
}

function formatDate(value) {
  if (!value) return 'Unknown';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString();
}

function statusStyle(result) {
  if (result === 'correct') return 'bg-green-100 text-green-800';
  if (result === 'discrepancy') return 'bg-red-100 text-red-800';
  if (result === 'review') return 'bg-amber-100 text-amber-900';
  return 'bg-gray-200 text-gray-800';
}

function statusLabel(result) {
  if (result === 'correct') return 'Matches';
  if (result === 'discrepancy') return 'Discrepancy';
  if (result === 'review') return 'Needs review';
  return 'Error';
}

function csvCell(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

export default function TrusteeFeeAudit() {
  const [open, setOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [credentialForm, setCredentialForm] = useState({
    username: '', password: '', creditor_name: '', trustee_code: 'TNKN', enabled: false,
  });
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [preflight, setPreflight] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('problems');
  const [expanded, setExpanded] = useState({});

  const applyCredentials = (data) => {
    setCredentials(data);
    setCredentialForm((current) => ({
      ...current,
      username: '',
      password: '',
      creditor_name: data?.creditor_name || '',
      trustee_code: data?.trustee_code || 'TNKN',
      enabled: Boolean(data?.enabled),
    }));
  };

  const loadPreflight = async (dateValue = startDate) => {
    const data = await apiFetch(`/firm/trustee-fee-audit/preflight/?start_date=${encodeURIComponent(dateValue)}`);
    setPreflight(data);
    setRun((current) => {
      if (current && ['queued', 'in_progress'].includes(current.status)) return current;
      return data.latest_run || current;
    });
    return data;
  };

  useEffect(() => {
    let active = true;
    Promise.all([
      apiFetch('/firm/trustee-fee-audit/credentials/'),
      apiFetch(`/firm/trustee-fee-audit/preflight/?start_date=${DEFAULT_START_DATE}`),
    ])
      .then(([credentialData, preflightData]) => {
        if (!active) return;
        applyCredentials(credentialData);
        setPreflight(preflightData);
        setRun(preflightData.latest_run || null);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'Could not load the trustee fee audit.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!run?.id || !['queued', 'in_progress'].includes(run.status)) return undefined;
    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const next = await apiFetch(`/firm/trustee-fee-audit/runs/${run.id}/`);
        if (!active) return;
        setRun(next);
        if (!['queued', 'in_progress'].includes(next.status)) {
          window.clearInterval(timer);
          await loadPreflight(next.start_date || startDate);
        }
      } catch (requestError) {
        if (active) setError(requestError.message || 'Could not refresh the trustee fee audit.');
      }
    }, 3000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id, run?.status]);

  const saveCredentials = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    const body = {
      creditor_name: credentialForm.creditor_name.trim(),
      trustee_code: credentialForm.trustee_code.trim(),
      enabled: credentialForm.enabled,
    };
    if (credentialForm.username.trim()) body.username = credentialForm.username.trim();
    if (credentialForm.password) body.password = credentialForm.password;
    try {
      const data = await apiFetch('/firm/trustee-fee-audit/credentials/', {
        method: 'PUT', body: JSON.stringify(body),
      });
      applyCredentials(data);
      setMessage('Trustee-site login saved.');
      await loadPreflight();
    } catch (requestError) {
      setError(requestError.message || 'Could not save the trustee-site login.');
    } finally {
      setSaving(false);
    }
  };

  const clearCredentials = async () => {
    if (!window.confirm('Remove the saved trustee-site username and password?')) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch('/firm/trustee-fee-audit/credentials/', { method: 'DELETE' });
      const blank = {
        encryption_ready: credentials?.encryption_ready,
        configured: false,
        ready: false,
        enabled: false,
        creditor_name: credentialForm.creditor_name,
        trustee_code: 'TNKN',
      };
      applyCredentials(blank);
      setMessage('Saved trustee-site login removed.');
      await loadPreflight();
    } catch (requestError) {
      setError(requestError.message || 'Could not remove the trustee-site login.');
    } finally {
      setSaving(false);
    }
  };

  const refreshCaseCount = async () => {
    setLoading(true);
    setError('');
    try {
      await loadPreflight(startDate);
    } catch (requestError) {
      setError(requestError.message || 'Could not count the eligible cases.');
    } finally {
      setLoading(false);
    }
  };

  const startAudit = async () => {
    const count = Number(preflight?.case_count) || 0;
    if (!window.confirm(
      `Sign in to trustee13.com and perform a read-only fee audit of ${count} filed or confirmed Chapter 13 case${count === 1 ? '' : 's'}?`,
    )) return;
    setStarting(true);
    setError('');
    setMessage('');
    try {
      const data = await apiFetch('/firm/trustee-fee-audit/start/', {
        method: 'POST',
        body: JSON.stringify({ confirmed: true, start_date: startDate }),
      });
      setRun(data);
      setMessage('The trustee fee audit has started. You can leave this page while it runs.');
    } catch (requestError) {
      setError(requestError.message || 'Could not start the trustee fee audit.');
    } finally {
      setStarting(false);
    }
  };

  const rows = Array.isArray(run?.results) ? run.results : [];
  const filteredRows = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'correct') return rows.filter((row) => row.result === 'correct');
    return rows.filter((row) => row.result !== 'correct');
  }, [rows, filter]);

  const downloadCsv = () => {
    const headers = [
      'Case Number', 'Debtors', 'Filed', 'Result', 'Message', 'Stored Total Fee',
      'Fees Paid Prior', 'Expected Remaining', 'Trustee Debt Total',
      'Trustee Principal Paid Plus Balance', 'Total Fee Variance', 'Remaining Fee Variance',
    ];
    const body = rows.map((row) => [
      row.case_number, row.debtor_names, row.date_filed, statusLabel(row.result), row.message,
      row.stored_total_fee, row.stored_fees_paid_prior, row.expected_remaining,
      row.trustee_debt_total, row.trustee_accounted_total,
      row.total_fee_variance, row.remaining_fee_variance,
    ]);
    const csv = [headers, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `chapter-13-trustee-fee-audit-${run?.completed_at?.slice(0, 10) || 'report'}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const issueCount = Number(run?.discrepancy_cases || 0)
    + Number(run?.review_cases || 0)
    + Number(run?.error_cases || 0);

  return (
    <section id="trustee-fee-audit" className="scroll-mt-6 overflow-hidden rounded-lg bg-white shadow">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left hover:bg-gray-50"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Chapter 13 Trustee Fee Audit</h2>
          <p className="mt-1 text-sm text-gray-600">
            Compare the attorney-fee claims and remaining balances on trustee13.com with each case.
          </p>
          <span className={`mt-3 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
            loading ? 'bg-gray-100 text-gray-700'
              : issueCount ? 'bg-red-100 text-red-800'
                : run?.completed_at ? 'bg-green-100 text-green-800'
                  : 'bg-indigo-100 text-indigo-800'
          }`}>
            {loading ? 'Loading…' : run?.completed_at
              ? `${issueCount} item${issueCount === 1 ? '' : 's'} need attention`
              : 'Not yet audited'}
          </span>
        </div>
        {open ? <ChevronUpIcon className="h-5 w-5 text-gray-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" />}
      </button>

      {open && (
        <div className="space-y-6 border-t border-gray-200 px-6 py-5">
          {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}
          {message && <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{message}</div>}

          {!credentials?.encryption_ready && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              The server credential-encryption key is not configured, so the trustee login cannot be saved yet.
            </div>
          )}

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2">
              <LockClosedIcon className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Trustee-site login</h3>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              This is separate from PACER. The audit only reads case information and logs out when finished.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium text-gray-700">
                Username
                <input
                  type="text"
                  autoComplete="username"
                  value={credentialForm.username}
                  onChange={(event) => setCredentialForm((current) => ({ ...current, username: event.target.value }))}
                  placeholder={credentials?.masked_username || 'Trustee-site username'}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Password
                <input
                  type="password"
                  autoComplete="new-password"
                  value={credentialForm.password}
                  onChange={(event) => setCredentialForm((current) => ({ ...current, password: event.target.value }))}
                  placeholder={credentials?.configured ? 'Leave blank to keep saved password' : 'Trustee-site password'}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Attorney creditor name on trustee site
                <input
                  type="text"
                  value={credentialForm.creditor_name}
                  onChange={(event) => setCredentialForm((current) => ({ ...current, creditor_name: event.target.value }))}
                  placeholder="Oliver Cisowski"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Trustee code
                <input
                  type="text"
                  value={credentialForm.trustee_code}
                  onChange={(event) => setCredentialForm((current) => ({ ...current, trustee_code: event.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 uppercase"
                />
              </label>
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={credentialForm.enabled}
                onChange={(event) => setCredentialForm((current) => ({ ...current, enabled: event.target.checked }))}
              />
              Enable read-only trustee fee audits
            </label>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={saveCredentials}
                disabled={saving || !credentials?.encryption_ready}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save Trustee Login'}
              </button>
              {credentials?.configured && (
                <button type="button" onClick={clearCredentials} disabled={saving} className="rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200">
                  Remove Saved Login
                </button>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900">Cases to audit</h3>
            <p className="mt-1 text-sm text-gray-600">
              Filed and confirmed Chapter 13 cases are included. June 1, 2025 is the recommended starting point because older imported case fee records may be incomplete.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="text-sm font-medium text-gray-700">
                Filing date on or after
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => {
                    setStartDate(event.target.value);
                    setPreflight(null);
                  }}
                  className="mt-1 block rounded-md border border-gray-300 px-3 py-2"
                />
              </label>
              <button type="button" onClick={refreshCaseCount} disabled={loading} className="rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200 disabled:opacity-50">
                Refresh Case Count
              </button>
              <button
                type="button"
                onClick={startAudit}
                disabled={starting || !preflight?.ready || ['queued', 'in_progress'].includes(run?.status)}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                {starting ? 'Starting…' : `Audit ${preflight?.case_count || 0} Cases`}
              </button>
            </div>
            {preflight && (
              <p className="mt-3 text-sm text-gray-600">
                {preflight.case_count} eligible case{preflight.case_count === 1 ? '' : 's'} · {preflight.missing_case_number_count} missing a case number · {preflight.missing_fee_data_count} missing stored fee data
              </p>
            )}
          </div>

          {run && (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Latest audit</h3>
                    <p className="mt-1 text-sm text-gray-600">{run.status_message || run.status_display}</p>
                    {run.error_message && <p className="mt-2 text-sm text-red-700">{run.error_message}</p>}
                  </div>
                  {rows.length > 0 && (
                    <button type="button" onClick={downloadCsv} className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200">
                      <ArrowDownTrayIcon className="h-4 w-4" /> Download CSV
                    </button>
                  )}
                </div>
                {['queued', 'in_progress'].includes(run.status) && (
                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200">
                    <div className="h-full bg-indigo-600 transition-all" style={{ width: `${run.total_cases ? Math.max(4, (run.processed_cases / run.total_cases) * 100) : 4}%` }} />
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
                  {[
                    ['Checked', run.processed_cases, 'text-gray-900'],
                    ['Correct', run.correct_cases, 'text-green-700'],
                    ['Discrepancies', run.discrepancy_cases, 'text-red-700'],
                    ['Review', run.review_cases, 'text-amber-700'],
                    ['Errors', run.error_cases, 'text-gray-700'],
                  ].map(([label, value, color]) => (
                    <div key={label} className="rounded-md bg-gray-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                      <div className={`mt-1 text-2xl font-bold ${color}`}>{value || 0}</div>
                    </div>
                  ))}
                </div>
              </div>

              {rows.length > 0 && (
                <div>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {[
                      ['problems', 'Needs attention'], ['all', 'All cases'], ['correct', 'Correct'],
                    ].map(([value, label]) => (
                      <button key={value} type="button" onClick={() => setFilter(value)} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${filter === value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {!filteredRows.length ? (
                    <div className="rounded-md border border-green-200 bg-green-50 px-4 py-5 text-sm text-green-800">
                      <CheckCircleIcon className="mr-2 inline h-5 w-5" /> No cases in this filter.
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 text-left text-gray-700">
                          <tr>
                            <th className="px-3 py-3 font-semibold">Case</th>
                            <th className="px-3 py-3 font-semibold">Result</th>
                            <th className="px-3 py-3 text-right font-semibold">Total fee</th>
                            <th className="px-3 py-3 text-right font-semibold">Trustee debt</th>
                            <th className="px-3 py-3 text-right font-semibold">Expected remaining</th>
                            <th className="px-3 py-3 text-right font-semibold">Trustee paid + balance</th>
                            <th className="px-3 py-3 font-semibold">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {filteredRows.map((row) => (
                            <React.Fragment key={row.case_id}>
                              <tr>
                                <td className="px-3 py-3 align-top">
                                  <a href={`/case/${row.case_id}`} className="font-semibold text-indigo-700 hover:underline">{row.case_number || row.case_id}</a>
                                  <div className="mt-1 text-gray-600">{row.debtor_names || 'Debtor name unavailable'}</div>
                                  <div className="text-xs text-gray-500">Filed {formatDate(row.date_filed)}</div>
                                </td>
                                <td className="px-3 py-3 align-top">
                                  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusStyle(row.result)}`}>{statusLabel(row.result)}</span>
                                  <div className="mt-2 max-w-xs text-xs text-gray-600">{row.message}</div>
                                </td>
                                <td className="px-3 py-3 text-right align-top">{money(row.stored_total_fee)}</td>
                                <td className="px-3 py-3 text-right align-top">
                                  {money(row.trustee_debt_total)}
                                  {row.total_fee_variance && Number(row.total_fee_variance) !== 0 && <div className="text-xs font-semibold text-red-700">{money(row.total_fee_variance, { signed: true })}</div>}
                                </td>
                                <td className="px-3 py-3 text-right align-top">
                                  {money(row.expected_remaining)}
                                  <div className="text-xs text-gray-500">Prior: {money(row.stored_fees_paid_prior)}</div>
                                </td>
                                <td className="px-3 py-3 text-right align-top">
                                  {money(row.trustee_accounted_total)}
                                  {row.remaining_fee_variance && Number(row.remaining_fee_variance) !== 0 && <div className="text-xs font-semibold text-red-700">{money(row.remaining_fee_variance, { signed: true })}</div>}
                                </td>
                                <td className="px-3 py-3 align-top">
                                  {row.fee_rows?.length ? (
                                    <button type="button" onClick={() => setExpanded((current) => ({ ...current, [row.case_id]: !current[row.case_id] }))} className="font-semibold text-indigo-700 hover:underline">
                                      {expanded[row.case_id] ? 'Hide rows' : `Show ${row.fee_rows.length} fee row${row.fee_rows.length === 1 ? '' : 's'}`}
                                    </button>
                                  ) : <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />}
                                </td>
                              </tr>
                              {expanded[row.case_id] && row.fee_rows?.length > 0 && (
                                <tr>
                                  <td colSpan="7" className="bg-gray-50 px-5 py-4">
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-xs">
                                        <thead className="text-left text-gray-600"><tr><th className="pb-2">Creditor</th><th className="pb-2">Claim</th><th className="pb-2 text-right">Debt</th><th className="pb-2 text-right">Principal paid</th><th className="pb-2 text-right">Balance</th><th className="pb-2 text-right">Code</th></tr></thead>
                                        <tbody>
                                          {row.fee_rows.map((feeRow, index) => (
                                            <tr key={`${feeRow.claim_number}-${index}`} className="border-t border-gray-200"><td className="py-2 pr-4">{feeRow.creditor_name}</td><td className="py-2 pr-4">{feeRow.claim_number || '—'}</td><td className="py-2 text-right">{money(feeRow.debt)}</td><td className="py-2 text-right">{money(feeRow.principal_paid)}</td><td className="py-2 text-right">{money(feeRow.balance)}</td><td className="py-2 text-right">{feeRow.disbursement_code || '—'}</td></tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    {row.unusual_row_count && <p className="mt-2 text-xs font-medium text-amber-800">This case has {row.fee_rows.length} matching attorney-fee row(s), rather than the usual two.</p>}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
